import React, { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { isAuthenticated, signIn } from '../auth';
import { login } from '../services/api';
import { trackEvent } from '../analytics';

const NAVY = '#1a2f5e';

// One row of the "funnel" — width shrinks as the count drops.
const FunnelRow = ({ widthClass, count, label, subLabel, highlight }) => (
  <div
    className={`${widthClass} mx-auto rounded-lg px-3 py-2 border ${
      highlight
        ? 'bg-emerald-500/25 border-emerald-300/60'
        : 'bg-white/10 border-white/15'
    }`}
  >
    <div className="flex items-center justify-center gap-2">
      {count !== undefined && count !== null ? (
        <span className="text-base md:text-lg font-bold tabular-nums leading-none">{count}</span>
      ) : null}
      <span
        className={`text-[11px] md:text-xs ${
          highlight ? 'font-semibold text-white' : 'text-blue-100'
        }`}
      >
        {label}
      </span>
    </div>
    {subLabel ? (
      <div className="mt-1 text-center text-[11px] font-semibold text-emerald-100">{subLabel}</div>
    ) : null}
  </div>
);

const DownChevron = () => (
  <div className="flex justify-center text-blue-300/70" aria-hidden="true">
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14" />
      <path d="M5 12l7 7 7-7" />
    </svg>
  </div>
);

// Down chevron + a small "+N newly past-due" note — shows continuous inflow.
const InflowMarker = ({ count }) => (
  <div className="flex flex-col items-center gap-0.5" aria-hidden="true">
    <svg className="h-4 w-4 text-blue-300/70" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14" />
      <path d="M5 12l7 7 7-7" />
    </svg>
    <span className="text-[10px] md:text-[11px] text-blue-200/70 leading-none">
      +{count} newly past-due
    </span>
  </div>
);

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

  if (isAuthenticated()) {
    const destination = location.state?.from?.pathname || '/business-impact';
    return <Navigate to={destination} replace />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { token, company_name: companyName } = await login(email, password);
      signIn(token, companyName);
      trackEvent('login', { method: 'password' });
      const destination = location.state?.from?.pathname || '/business-impact';
      navigate(destination, { replace: true });
    } catch (err) {
      const message = err?.response?.data?.error || 'Unable to sign in. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async () => {
    setError('');
    setDemoLoading(true);
    // Reflect the demo creds in the form so the user sees what just happened.
    setEmail('demo@sunshinebackflow.com');
    setPassword('demo1234');
    try {
      const { token, company_name: companyName } = await login(
        'demo@sunshinebackflow.com',
        'demo1234'
      );
      signIn(token, companyName);
      trackEvent('login', { method: 'demo' });
      const destination = location.state?.from?.pathname || '/business-impact';
      navigate(destination, { replace: true });
    } catch (err) {
      const message = err?.response?.data?.error || 'Unable to sign in. Please try again.';
      setError(message);
    } finally {
      setDemoLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left / brand + pitch panel */}
      <div
        className="md:w-1/2 text-white flex flex-col px-6 py-6 md:px-10 md:py-8"
        style={{ backgroundColor: NAVY }}
      >
        {/* Top: compact logo + wordmark */}
        <div className="flex items-center gap-3">
          <svg className="h-9 w-9 flex-shrink-0" viewBox="0 0 64 64" fill="none" aria-hidden="true">
            <path
              d="M32 4C32 4 14 24 14 38a18 18 0 0036 0C50 24 32 4 32 4z"
              fill="rgba(255,255,255,0.08)"
              stroke="#ffffff"
              strokeWidth="2.5"
              strokeLinejoin="round"
            />
            <path
              d="M32 22c-3.5 4-6 8-6 11.5A6 6 0 0032 39"
              stroke="#9cc3ff"
              strokeWidth="2.5"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
          <span className="text-xl font-bold tracking-tight">BackflowOffice</span>
        </div>

        {/* Middle: value prop, funnel, supporting line */}
        <div className="flex-1 flex flex-col justify-center w-full max-w-2xl mx-auto mt-4 md:mt-0">
          <h2 className="text-xl md:text-2xl font-semibold leading-snug">
            Most backflow companies don&rsquo;t have a collections problem.
          </h2>
          <p className="mt-2 text-xl md:text-2xl font-semibold leading-snug text-center">
            They have a <span className="text-sky-300">follow-up</span> problem.
          </p>

          {/* Dual funnels: Follow-Up Activity (decreasing) ↔ Overdue Invoices Paid (increasing) */}
          <div className="mt-4 grid grid-cols-2 gap-3 md:gap-4">
            {/* Left: Follow-Up Activity */}
            <div>
              <h3 className="text-center text-[10px] md:text-[11px] uppercase tracking-wider font-semibold text-blue-200 leading-tight">
                Follow-Up Activity
              </h3>
              <p className="text-center text-[9px] md:text-[10px] text-blue-200/60 mb-3 leading-tight">
                Past 30+ Days Due
              </p>
              <div className="space-y-1">
                <FunnelRow widthClass="w-full" count="103" label="Past Due Invoices" />
                <InflowMarker count="8" />
                <FunnelRow widthClass="w-4/5" count="72" label="Left to Contact" />
                <InflowMarker count="12" />
                <FunnelRow widthClass="w-3/5" count="41" label="Left to Contact" />
                <InflowMarker count="9" />
                <FunnelRow widthClass="w-2/5" count="18" label="Left to Contact" />
                <InflowMarker count="11" />
                <FunnelRow widthClass="w-2/5" count="~15" label="Active · Caught Up" highlight />
              </div>
            </div>

            {/* Right: Overdue Invoices Paid */}
            <div>
              <h3 className="text-center text-[10px] md:text-[11px] uppercase tracking-wider font-semibold text-emerald-300 leading-tight">
                Overdue Invoices Paid
              </h3>
              <p className="text-center text-[9px] md:text-[10px] text-emerald-200/60 mb-3 leading-tight">
                Cumulative · 90 Days
              </p>
              <div className="space-y-1">
                <FunnelRow widthClass="w-1/3" count="0" label="Paid" />
                <DownChevron />
                <FunnelRow widthClass="w-2/5" count="10" label="Paid" />
                <DownChevron />
                <FunnelRow widthClass="w-3/5" count="30" label="Paid" />
                <DownChevron />
                <FunnelRow widthClass="w-4/5" count="57" label="Paid" />
                <DownChevron />
                <FunnelRow widthClass="w-full" count="73" label="Paid" subLabel="$48,200 Collected" highlight />
              </div>
            </div>
          </div>

          <p className="mt-3 text-center text-[11px] md:text-xs italic text-blue-200/85 leading-snug">
            Past-due isn&rsquo;t a problem you solve once — it&rsquo;s a tide you stay ahead of.
          </p>

          <p className="mt-3 text-sm text-blue-100 leading-relaxed">
            Upload your BSI export and give your staff a simple process to work every invoice,
            quote, and upcoming test.
          </p>

          <div className="mt-3 rounded-xl border border-white/20 bg-white/10 px-4 py-3">
            <p className="text-xs uppercase tracking-wider font-semibold text-blue-200 mb-2">Typical BSI company</p>
            <p className="text-sm text-blue-100 leading-relaxed">
              A typical BSI company we see has <span className="text-white font-semibold">$40,000–$50,000 in accounts receivable</span>, with most of it past due and <span className="text-white font-semibold">100+ open quotes</span> nobody is systematically following up on. That&rsquo;s not a collections problem — it&rsquo;s a follow-up problem.
            </p>
          </div>
        </div>

        {/* Bottom tagline */}
        <p className="mt-4 text-center text-xs font-medium tracking-wide text-blue-200">
          Invoices · Quotes · Upcoming Tests
        </p>
      </div>

      {/* Right / form panel */}
      <div className="md:w-1/2 bg-white flex items-center justify-center px-6 py-6">
        <div className="w-full max-w-md">
          <h1
            className="text-center text-3xl md:text-4xl font-extrabold tracking-tight"
            style={{ color: NAVY }}
          >
            BSI READY
          </h1>
          <div className="mt-4 text-center">
            <h2 className="text-xl font-bold text-slate-900">Sign In</h2>
            <p className="mt-1 text-xs text-slate-500">Welcome back. Please sign in to continue.</p>
          </div>

          {/* Demo access banner — one-click entry for cold visitors. Teal palette
              keeps it visually distinct from the emerald "Free Pilot" panel below. */}
          <div className="mt-5 rounded-xl border-2 border-teal-400 bg-teal-50 p-4 shadow-sm">
            <div className="flex items-start gap-2">
              <span className="text-xl leading-none mt-0.5" aria-hidden="true">👉</span>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm md:text-base font-bold text-teal-900">
                  Try the Live Demo — No Signup Needed
                </h3>
                <p className="mt-1 text-xs md:text-sm text-teal-800 leading-snug">
                  Click below to instantly log in with our demo account and explore real BSI data.
                </p>
                <button
                  type="button"
                  onClick={handleDemoLogin}
                  disabled={demoLoading || loading}
                  className="mt-3 w-full md:w-auto px-4 py-2.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors"
                >
                  {demoLoading ? 'Entering…' : 'Enter Live Demo →'}
                </button>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="mt-5 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-slate-700">Email</label>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="w-full p-3 border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoComplete="username"
                placeholder="you@company.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-slate-700">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="w-full p-3 pr-12 border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoComplete="current-password"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-500 hover:text-slate-700"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 3l18 18" />
                      <path d="M10.58 10.58a2 2 0 102.83 2.83" />
                      <path d="M9.88 5.09A10.94 10.94 0 0112 4c5 0 9.27 3.11 11 8-1 2.8-3.01 5.08-5.58 6.32" />
                      <path d="M6.61 6.61C4.62 8 3.07 9.84 2 12c1.01 2.84 3.08 5.15 5.74 6.39" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error ? (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={loading || demoLoading}
              className="w-full text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed hover:brightness-110"
              style={{ backgroundColor: NAVY }}
            >
              {loading ? 'Signing In…' : 'Sign In'}
            </button>
          </form>

          <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-3 text-center">
            <p className="text-sm font-semibold text-slate-800">Limited FREE Pilot — 5 spots open</p>
            <p className="mt-1 text-xs text-slate-600 leading-relaxed">
              We set up your account, train your team, and you run it free for 45 days on your real BSI data.
            </p>
            <a
              href="https://calendly.com/dennisyd/15-minute-discovery-call"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-block rounded-lg px-4 py-2 text-sm font-semibold text-white transition-colors hover:brightness-110"
              style={{ backgroundColor: '#10b981' }}
            >
              Book a free 15-minute setup call →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
